import { ref, computed, type Ref } from 'vue';
import type { LienData } from '@/models/lienTypes';
import {
  canPushLien,
  canRetryLien,
  canUndoLien,
  isPushActionable,
  isPushBlocked,
  isPushed,
} from '@/utils/lienHelpers';

/**
 * Row-selection state for the Liens tab bulk-action bar.
 *
 * Selection lives in the component (NOT the Pinia store) and resets on
 * navigation and after successful bulk operations. All derived flags are
 * pure computeds over the current page of liens, so they stay correct as the
 * underlying data refreshes.
 *
 * When `isSettlementLocked` is true, "select all" is scoped to push/undo
 * eligible liens only — non-actionable rows have disabled checkboxes and
 * should never end up in the selection.
 */
export function useLienSelection(
  liens: Ref<LienData[]>,
  isSettlementLocked?: Ref<boolean>,
) {
  const selectedIds = ref<string[]>([]);

  // O(1) membership — `isSelected` is called once per row on every render,
  // so backing it with a Set keeps table rendering linear, not quadratic.
  const selectedSet = computed(() => new Set(selectedIds.value));

  const selectedLiens = computed(() =>
    liens.value.filter((l) => selectedSet.value.has(l.lienId)),
  );

  // When locked, "all" means all push/undo-eligible liens; otherwise all liens — EXCEPT a
  // pushed lien that's proactively blocked from Undo (sc-46091: QBO Cleared/Reconciled), which
  // is never selectable for bulk regardless of lock state (there's nothing to do with it).
  const selectableWhenLocked = computed(() => {
    const base = isSettlementLocked?.value
      ? liens.value.filter((l) => isPushActionable(l) || canUndoLien(l))
      : liens.value;
    return base.filter((l) => !(isPushed(l.status) && !canUndoLien(l)));
  });

  const allSelected = computed(() => {
    const selectable = selectableWhenLocked.value;
    return (
      selectable.length > 0 &&
      selectable.every((l) => selectedSet.value.has(l.lienId))
    );
  });

  /** Partial selection — drives the header checkbox's indeterminate state. */
  const someSelected = computed(
    () => selectedIds.value.length > 0 && !allSelected.value,
  );

  const hasPushableSelected = computed(() =>
    selectedLiens.value.some(isPushActionable),
  );

  const hasUndoableSelected = computed(() =>
    selectedLiens.value.some(canUndoLien),
  );

  /** True if at least one lien in the full list is eligible for push or undo (drives header checkbox disabled state when locked). */
  const hasAnyPushableOrUndoable = computed(() =>
    liens.value.some((l) => isPushActionable(l) || canUndoLien(l)),
  );

  /** Ids eligible for a (bulk) push — finalized+notPushed, or failed (retry). */
  const pushableSelectedIds = computed(() =>
    selectedLiens.value.filter(isPushActionable).map((l) => l.lienId),
  );

  /** Selected liens a bulk push leaves out because trust can't cover them. */
  const blockedSelectedCount = computed(
    () => selectedLiens.value.filter(isPushBlocked).length,
  );

  /** Ids eligible for a (bulk) undo — currently pushed. */
  const undoableSelectedIds = computed(() =>
    selectedLiens.value.filter(canUndoLien).map((l) => l.lienId),
  );

  function isSelected(lienId: string): boolean {
    return selectedSet.value.has(lienId);
  }

  function toggle(lienId: string): void {
    selectedIds.value = isSelected(lienId)
      ? selectedIds.value.filter((id) => id !== lienId)
      : [...selectedIds.value, lienId];
  }

  function toggleAll(value: boolean): void {
    selectedIds.value = value
      ? selectableWhenLocked.value.map((l) => l.lienId)
      : [];
  }

  function clear(): void {
    selectedIds.value = [];
  }

  /** Drop ids no longer present after a data refresh (keeps badge accurate). */
  function prune(): void {
    const present = new Set(liens.value.map((l) => l.lienId));
    selectedIds.value = selectedIds.value.filter((id) => present.has(id));
  }

  return {
    selectedIds,
    selectedLiens,
    allSelected,
    someSelected,
    hasPushableSelected,
    hasUndoableSelected,
    hasAnyPushableOrUndoable,
    pushableSelectedIds,
    blockedSelectedCount,
    undoableSelectedIds,
    isSelected,
    toggle,
    toggleAll,
    clear,
    prune,
    // re-exported predicates for template convenience
    canPushLien,
    canRetryLien,
    canUndoLien,
    isPushActionable,
  };
}
